import { authorizedFetch, getCurrentAccessToken } from "./apiService";
import { normalizeTranscript, formatDateTime } from "../utils";

const TRANSCRIPT_API_URL = process.env.REACT_APP_TRANSCRIPT_API_URL;

// Callback refs (set by App.js) for state updates
let callbacks = {
  setLoadingTranscript: null,
  setTranscript: null,
  setSummary: null,
  setContactId: null,
  setCallStartTime: null,
  setLastUpdated: null,
  setCallStatus: null,
  setIsLiveCall: null,
  setJournalId: null,
  setIsSummaryGenerated: null,
  setCaseNumberList: null,
  setAgentNotes: null,
  onInitWS: null,
};

export function initTranscriptService(cbs) {
  callbacks = { ...callbacks, ...cbs };
}

// Main: fetch transcript via REST and handle STARTED vs COMPLETED
export async function getTranscript(callIdParam, { accessToken, agentExtension, setAgentExtension }) {
  if (!callIdParam) {
    return;
  }

  if (callbacks.setLoadingTranscript) callbacks.setLoadingTranscript(true);
  if (callbacks.setTranscript) callbacks.setTranscript("");
  if (callbacks.setSummary) callbacks.setSummary("");

  try {
    const res = await authorizedFetch(TRANSCRIPT_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ContactId: callIdParam }),
    });

    if (!res.ok) {
      throw new Error("Failed to fetch transcript");
    }

    const data = await res.json();

    const { data: d = {} } = data;
    const calls = d.Calls || {};

    const isSummaryGenerated =
      d.isSummaryGenerated ?? calls.isSummaryGenerated ?? false;

    if (callbacks.setIsSummaryGenerated) {
      callbacks.setIsSummaryGenerated(!!isSummaryGenerated);
    }

    // Extract CaseNumberList — may be a string, array, or object depending on backend
    const rawCaseNumberList = d.CaseNumberList ?? calls.CaseNumberList ?? null;
    if (callbacks.setCaseNumberList && rawCaseNumberList != null) {
      callbacks.setCaseNumberList(rawCaseNumberList);
    }

    // Extract journalId if present
    if (d.journalId || calls.journalId) {
      const jId = d.journalId || calls.journalId;
      if (callbacks.setJournalId) callbacks.setJournalId(jId);
    }

    // Extract agent notes
    if (callbacks.setAgentNotes) {
      callbacks.setAgentNotes(d.Notes || calls.Notes || "");
    }

    const contact = d.ContactId || calls.ContactId || d.UserID || null;
    const extractedUserId = d.UserID || calls.UserID || null;

    if (extractedUserId && setAgentExtension) {
      setAgentExtension(extractedUserId);
    }

    const startTime = d.call_start_time || calls.call_start_time || null;
    const lastTime = d.last_updated || calls.last_updated || null;

    // Determine call status
    const rawStatus = d.call_status || calls.call_status || "";
    const normalizedStatus = rawStatus ? rawStatus.trim().toUpperCase() : "";
    if (callbacks.setCallStatus) callbacks.setCallStatus(normalizedStatus || null);

    if (callbacks.setContactId) callbacks.setContactId(contact);
    if (callbacks.setCallStartTime) callbacks.setCallStartTime(formatDateTime(startTime));
    if (callbacks.setLastUpdated) callbacks.setLastUpdated(formatDateTime(lastTime));

    // Prepare normalized REST transcript (may be empty)
    const normalizedTranscript = normalizeTranscript(data);

    // 🔵 LIVE CALL (STARTED) → use WS only, backend sends all messages
    if (normalizedStatus === "STARTED") {
      if (callbacks.setIsLiveCall) callbacks.setIsLiveCall(true);

      const userIdForWs = extractedUserId || contact || callIdParam;
      const currentToken = getCurrentAccessToken() || accessToken;
      const wsPayload = {
        action: "getTranscript",
        userId: userIdForWs,
        UserID: extractedUserId || null,
        ContactId: contact || null,
        call_status: rawStatus || null,
        call_start_time: startTime || null,
        last_updated: lastTime || null,
        CaseNumberList: rawCaseNumberList || null,
      };
      if (currentToken) wsPayload.token = currentToken;

      if (callbacks.onInitWS) {
        callbacks.onInitWS(callIdParam, wsPayload);
      }
      return;
    }

    // COMPLETED or other statuses
    if (callbacks.setIsLiveCall) callbacks.setIsLiveCall(false);

    if (callbacks.setTranscript) callbacks.setTranscript(normalizedTranscript);
    if (callbacks.setSummary) callbacks.setSummary("");

  } catch (err) {
    if (callbacks.setTranscript) callbacks.setTranscript("No transcript available");
  } finally {
    if (callbacks.setLoadingTranscript) callbacks.setLoadingTranscript(false);
  }
}
